import React, { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, ReferenceArea } from "recharts";
import { http } from "@/lib/api";
import { TrendingUp } from "lucide-react";

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return ""; }
};

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="panel-elevated p-2 text-xs font-mono">
      <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">{fmtTime(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2" style={{ background: p.color }} />
          <span>{p.dataKey}</span>
          <span className="ml-auto">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export const ForecastChart = ({ zoneId, zoneName, horizonMin = 60 }) => {
  const [series, setSeries] = useState(null);

  useEffect(() => {
    if (!zoneId) return;
    let alive = true;
    http.get(`/predictive/zone/${zoneId}?horizon_min=${horizonMin}`)
      .then((r) => { if (alive) setSeries(r.data); })
      .catch(() => { if (alive) setSeries(null); });
    return () => { alive = false; };
  }, [zoneId, horizonMin]);

  const data = useMemo(() => {
    if (!series) return [];
    const past = (series.history || []).map((p) => ({ ts: p.ts, actual: p.value }));
    const future = (series.future || []).map((p) => ({ ts: p.ts, projected: p.value }));
    return [...past, ...future];
  }, [series]);

  const forecastStart = series?.future?.[0]?.ts;
  const cap = series?.capacity ?? 0;

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="forecast-chart">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="h-widget truncate">Forecast · {zoneName || zoneId || "—"}</span>
        </div>
        <span className="font-mono text-[10px] text-muted-foreground">
          history {series?.history?.length ?? 0} · projected {series?.future?.length ?? 0}
        </span>
      </div>
      <div className="flex-1 p-2 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="ts" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" tickFormatter={fmtTime} minTickGap={28} />
            <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" width={36} />
            <Tooltip content={<TooltipBox />} />
            {cap > 0 && (
              <ReferenceLine y={cap} stroke="hsl(var(--destructive))" strokeDasharray="3 3" label={{ value: "capacity", fontSize: 9, fontFamily: "IBM Plex Mono", fill: "hsl(var(--destructive))", position: "insideTopRight" }} />
            )}
            {forecastStart && (
              <ReferenceArea x1={forecastStart} x2={data[data.length - 1]?.ts} fill="hsl(var(--primary))" fillOpacity={0.06} />
            )}
            <Line type="monotone" dataKey="actual" stroke="hsl(var(--primary))" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="projected" stroke="hsl(var(--warning))" strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ForecastChart;
